<template>
  <div class="relative flex min-h-screen w-full flex-col items-center justify-center bg-base dark:bg-background-dark p-4">
    <div class="w-full max-w-lg rounded-xl bg-surface dark:bg-black/15 backdrop-blur-sm p-8 shadow-2xl border border-line dark:border-roundBorder">
      
      <!-- Header -->
      <div class="flex flex-col items-center">
        <!-- 테넌트 전용 로고 (kogas 등). 설정 없으면 미표시 -->
        <img
          v-if="loginBrand && loginBrand.logo"
          :src="loginBrand.logo"
          :alt="loginBrand.logoAlt"
          class="mb-4 max-h-16 w-auto max-w-full object-contain"
        />
        <h1 class="text-content
           text-title
           font-bold
           tracking-tight
           leading-tight
           text-center
           pb-2 pt-4">
          {{ (loginBrand && loginBrand.title) || $t('login.title') }}
        </h1>
        <p class="text-content
           text-title-sub
           font-normal
           leading-normal
           pb-8
           text-center">
          {{ (loginBrand && loginBrand.subtitle) || $t('login.subtitle') }}
        </p>
      </div>

      <!-- Form -->
      <form @submit.prevent="authLogin">
        <div class="flex flex-col gap-6">
          <!-- ID -->
          <div class="flex flex-col">
            <label class="text-content text-form-label leading-normal pb-2">
              {{ $t('login.id') }}
            </label>
            <div :class="[
              'flex w-full items-center rounded-lg h-12 bg-base',
              !idValidFlag ? 'border-red-500' : 'border dark:border-roundBorder'
            ]">
              <div class="flex items-center justify-center pl-4 text-muted">
                <span class="material-symbols-outlined text-icon-sm">badge</span>
              </div>
              <input
                v-model="UserId"
                id="UID"
                type="text"
                name="mdt-login-id"
                autocomplete="off"
                :class="[
                  'form-input flex-1 h-full border-none bg-transparent text-content focus:outline-0 focus:ring-0 placeholder:text-muted px-4 pl-2 text-base font-normal leading-normal'
                ]"
                :placeholder="$t('login.idPlaceholder')"
              />
            </div>
            <p v-if="!idValidFlag" class="mt-1 text-sm text-red-400">{{ $t('login.idRequired') }}</p>
          </div>

          <!-- Password Field -->
          <div class="flex flex-col">
            <label class="text-content text-form-label leading-normal pb-2">
              {{ $t('login.password') }}
            </label>
            <div :class="[
              'flex w-full items-center rounded-lg h-12 bg-base',
              !pwValidFlag ? 'border-red-500' : 'border dark:border-roundBorder'
            ]">
              <div class="flex items-center justify-center pl-4 text-muted">
                <span class="material-symbols-outlined text-icon-sm">lock</span>
              </div>
              <input
                v-model="UserPassword"
                id="UPW"
                type="password"
                autocomplete="off"
                :class="[
                  'form-input flex-1 h-full border-none bg-transparent text-content focus:outline-0 focus:ring-0 placeholder:text-muted px-4 pl-2 text-base font-normal leading-normal'
                ]"
                :placeholder="$t('login.passwordPlaceholder')"
              />
            </div>
            <p v-if="!pwValidFlag" class="mt-1 text-sm text-red-400">{{ $t('login.passwordRequired') }}</p>
          </div>

          <!-- Remember ID & Forgot Password -->
          <div class="flex items-center justify-between -mt-3">
            <div class="flex items-center">
              <input
                @click="checkbox"
                type="checkbox"
                v-model="rememberId"
                value="checked"
                name="rememberId"
                id="rememberId"
                class="remember-id-check h-4 w-4 cursor-pointer"
              />
              <label for="rememberId" 
                class="ml-2 text-title-sub-xs text-content cursor-pointer"
                >
                {{ $t('login.rememberId') }}
              </label>
            </div>
            <p
              v-if="!(loginBrand && loginBrand.hideForgotPassword)"
              @click="$router.push('/forgot-password')"
              class="text-primary/90 text-title-sub-xs leading-normal underline cursor-pointer hover:text-primary transition-colors"
            >
              {{ $t('login.forgotPassword') }}
            </p>
          </div>
        </div>

        <!-- Login Button -->
        <div class="mt-8">
          <button
            type="submit"
            class="flex w-full items-center justify-center rounded-lg 
                bg-primary h-12 px-6 
                text-grid-btn font-bold text-content shadow-lg 
                shadow-primary/20 transition-all duration-300 
                hover:bg-primary/90 focus:outline-none focus:ring-2 
                focus:ring-primary focus:ring-offset-2 
                focus:ring-offset-background-dark"
          >
            {{ $t('login.loginButton') }}
          </button>
        </div>

        <!-- Sign Up Link -->
        <div v-if="!(loginBrand && loginBrand.hideSignUp)" class="mt-8 text-center">
          <p class="text-muted text-sm font-normal">
            {{ $t('login.noAccount') }}
            <span
              @click="$router.push('/signup')"
              class="font-semibold text-primary/90 underline hover:text-primary transition-colors cursor-pointer"
            >
              {{ $t('login.signUp') }}
            </span>
          </p>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
  import { onMounted, ref, watch } from 'vue';
  import { useStore } from "vuex";
  import { useI18n } from 'vue-i18n';
  import VueCookie from 'vue-cookies';
  import axios from "axios";
  import { AUTH_URL } from "@/config";
  import { loginBrand } from "@/config/tenantBranding";
  const store = useStore();
  const { t } = useI18n();

  const UserId = ref('');
  const UserPassword = ref('');

  let idValidFlag = ref(true);
  let pwValidFlag = ref(true);
  let loginValidFlag = ref(false);
  let rememberId = ref(false);

  // "아이디 저장" 체크를 끄면 즉시 저장된 아이디 삭제 (체크 안 하면 아무것도 남기지 않음)
  watch(rememberId, (checked) => {
    if (!checked) {
      VueCookie.remove("idCookie");
    }
  });

  async function authLogin() {
    await validCheck();

    if (loginValidFlag.value == true) {
      if (rememberId.value == true) {
        VueCookie.set("idCookie", UserId.value);
      }
      else {
        VueCookie.remove("idCookie");
      }

      let loginData = {};
      loginData.UserId = UserId.value;
      loginData.UserPassword = UserPassword.value;
      store.dispatch('login', loginData);
    }
  }

  async function validCheck() {
    if (UserId.value == "") {
      idValidFlag.value = false;
      pwValidFlag.value = true;
      loginValidFlag.value = false
    }
    else if (UserPassword.value == "") {
      idValidFlag.value = true;
      pwValidFlag.value = false;
      loginValidFlag.value = false
    }
    else if (UserId.value != "" && UserPassword.value != "") {
      loginValidFlag.value = true
      return true
    }
  }

  function checkbox() {
  }

  onMounted(() => {
    // 마지막 세션 정리 — 이전 세션이 정상 로그아웃 안 됨(lastLoginSession='LOGIN' = 비정상 종료)이고
    // refresh_token 이 남아있으면, 서버 세션을 LOGOUT 으로 정리(중복 로그인 흔적 제거).
    // 정상 로그아웃(='LOGOUT')이면 스킵. (로그인 화면 = 앱 관점상 이미 로그아웃 상태)
    const lastSession = localStorage.getItem("lastLoginSession");
    const refreshToken = localStorage.getItem("refreshToken");
    if (lastSession === "LOGIN" && refreshToken) {
      axios.post(`${AUTH_URL}/logout`, { refresh_token: refreshToken }).catch(() => {});
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      delete axios.defaults.headers.common.Authorization;
      localStorage.setItem("lastLoginSession", "LOGOUT");
    }

    const idCookies = VueCookie.get("idCookie");
    if (idCookies) {
      UserId.value = idCookies;
      rememberId.value = true;
    }
  })
</script>

<style scoped>
  /* 아이디 저장 체크박스: @tailwindcss/forms + Bootstrap 충돌로 체크 시 색으로 덮여 보임.
     배경 채움 없이(투명) 테두리 박스 + 파란 체크마크만 표시되도록 커스텀. */
  .remember-id-check {
    appearance: none !important;
    -webkit-appearance: none !important;
    width: 1rem !important;
    height: 1rem !important;
    border: 1.5px solid rgb(var(--c-muted)) !important;
    border-radius: 3px !important;
    background-color: transparent !important;
    background-image: none !important;
    cursor: pointer;
  }
  .remember-id-check:checked {
    border-color: #137fec !important;
    background-color: transparent !important;
    background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M3 8l3.5 3.5L13 5' stroke='%23137fec' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'/%3e%3c/svg%3e") !important;
    background-size: 88% 88% !important;
    background-position: center !important;
    background-repeat: no-repeat !important;
  }
  /* 전역 _inputs.scss 의 input[type=checkbox]::before(체크 시 인디고 #3f51b5 사각형이 커지며 내부를 채움)을
     이 체크박스에서만 제거 — 색 채움 없이 테두리 박스 + 파란 체크마크만 유지 */
  .remember-id-check::before {
    display: none !important;
    content: none !important;
  }
  .remember-id-check:focus,
  .remember-id-check:focus-visible {
    outline: none !important;
    box-shadow: none !important;
  }

  /* Override browser autofill styles */
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px rgb(var(--c-surface)) inset !important;
    -webkit-text-fill-color: rgb(var(--c-content)) !important;
    caret-color: rgb(var(--c-content)) !important;
    transition: background-color 5000s ease-in-out 0s;
  }
</style>